// === Admin Portal Auth Callback ===
// Landing page for magic links addressed to a SUPERADMIN — invites and password
// links, which apps/api/src/routes/users.ts points here (company users go to the
// web app's /auth/handoff instead). Normal login is email + password and does
// not pass through this route.
//
// Supabase delivers the user here with the session in the URL fragment
// (#access_token=...). Supabase JS picks it up automatically on client init; we
// just wait for SIGNED_IN and bounce to /dashboard. Errors (e.g. expired link)
// are surfaced with a button back to /login.

import { useEffect, useState } from 'react';
import { Box, Button, Typography, CssBaseline, CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../lib/auth';
import { APP_VERSION } from '../version';

const HANDOFF_TIMEOUT_MS = 10_000;
const LOGO_URL = 'https://click2gro.com.au/assets/images/Click2gro_white.png';

interface ParsedHash {
  hasError: boolean;
  errorCode: string | undefined;
  errorDescription: string | undefined;
}

function parseHash(): ParsedHash {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  if (!hash) return { hasError: false, errorCode: undefined, errorDescription: undefined };
  const params = new URLSearchParams(hash);
  if (params.get('error') || params.get('error_code')) {
    return {
      hasError: true,
      errorCode: params.get('error_code') ?? params.get('error') ?? undefined,
      errorDescription: params.get('error_description')?.replace(/\+/g, ' ') ?? undefined,
    };
  }
  return { hasError: false, errorCode: undefined, errorDescription: undefined };
}

function friendlyError(code: string | undefined, description: string | undefined): { title: string; body: string } {
  switch (code) {
    case 'otp_expired':
      return {
        title: 'This return link has expired',
        body: 'The handoff link expired before it could be used. Sign in again with your email and password.',
      };
    case 'access_denied':
      return {
        title: 'Return handoff was denied',
        body: description ?? 'The handoff link is no longer valid. Sign in again with your email and password.',
      };
    case 'server_error':
      return {
        title: 'The auth server hit an error',
        body: description ?? 'Something went wrong on our side. Sign in again with your email and password.',
      };
    default:
      return {
        title: 'Sign-in failed',
        body: description ?? "We couldn't complete the handoff. Sign in again with your email and password.",
      };
  }
}

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'error'>('pending');
  const [errorTitle, setErrorTitle] = useState('');
  const [errorBody, setErrorBody] = useState('');

  useEffect(() => {
    const parsed = parseHash();
    if (parsed.hasError) {
      const f = friendlyError(parsed.errorCode, parsed.errorDescription);
      setErrorTitle(f.title);
      setErrorBody(f.body);
      setStatus('error');
      return;
    }

    let unsubscribed = false;
    let unsub: (() => void) | undefined;
    const timer = setTimeout(() => {
      unsub?.();
      if (!unsubscribed) {
        setErrorTitle('Sign-in is taking longer than expected');
        setErrorBody("We didn't hear back from the auth server. Sign in again with your email and password.");
        setStatus('error');
      }
    }, HANDOFF_TIMEOUT_MS);

    void (async () => {
      try {
        const supabase = await getSupabaseClient();
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          unsubscribed = true;
          clearTimeout(timer);
          navigate('/dashboard', { replace: true });
          return;
        }
        const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (event === 'SIGNED_IN' && newSession) {
            unsubscribed = true;
            clearTimeout(timer);
            listener.subscription.unsubscribe();
            navigate('/dashboard', { replace: true });
          }
        });
        unsub = () => listener.subscription.unsubscribe();
      } catch (e) {
        clearTimeout(timer);
        setErrorTitle('Sign-in failed');
        setErrorBody(e instanceof Error ? e.message : 'An unexpected error occurred. Please try again.');
        setStatus('error');
      }
    })();

    return () => {
      unsubscribed = true;
      clearTimeout(timer);
      unsub?.();
    };
  }, [navigate]);

  return (
    <>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a1929',
        backgroundImage: 'linear-gradient(135deg, #0a1929 0%, #102a43 100%)',
        px: 3,
        py: 4,
      }}>
        <Box component="img" src={LOGO_URL} alt="Click2Gro" sx={{ height: 56, mb: 4 }} />

        <Box sx={{
          width: '100%',
          maxWidth: 460,
          p: 4,
          borderRadius: 2,
          border: '1px solid rgba(94, 146, 243, 0.15)',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(8px)',
          textAlign: 'center',
        }}>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            mb: 3,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            border: '1px solid rgba(94, 146, 243, 0.3)',
            backgroundColor: 'rgba(21, 101, 192, 0.08)',
          }}>
            <AdminPanelSettingsIcon sx={{ color: '#5e92f3', fontSize: 16 }} />
            <Typography variant="caption" sx={{
              color: '#5e92f3',
              fontSize: '0.65rem',
              letterSpacing: 1.5,
              textTransform: 'uppercase',
            }}>
              Admin Portal
            </Typography>
          </Box>

          {status === 'pending' ? (
            <>
              <CircularProgress size={36} sx={{ color: '#5e92f3', mb: 3 }} />
              <Typography sx={{ color: '#ecf0f1', fontSize: '1.25rem', fontWeight: 300, mb: 1 }}>
                Returning to admin
              </Typography>
              <Typography sx={{ color: '#95a5a6', fontSize: '0.875rem' }}>
                Restoring your superadmin session…
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                border: '1px solid rgba(231, 76, 60, 0.3)',
                mb: 2,
              }}>
                <ErrorOutlineIcon sx={{ color: '#e74c3c', fontSize: 28 }} />
              </Box>
              <Typography sx={{ color: '#ecf0f1', fontSize: '1.25rem', fontWeight: 500, mb: 1.5 }}>
                {errorTitle}
              </Typography>
              <Typography sx={{ color: '#bdc3c7', fontSize: '0.9rem', lineHeight: 1.6, mb: 3 }}>
                {errorBody}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login', { replace: true })}
                sx={{
                  backgroundColor: '#1565c0',
                  color: '#ffffff',
                  py: 1.25,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 1,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#003c8f', boxShadow: 'none' },
                }}
              >
                Back to sign-in
              </Button>
            </>
          )}
        </Box>

        <Typography sx={{
          color: '#7f8c8d',
          fontSize: '0.625rem',
          mt: 4,
          textAlign: 'center',
        }}>
          Copyright © Click2Gro 2025 · {APP_VERSION}
        </Typography>
      </Box>
    </>
  );
}
